'use client';

import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { CheckCircleIcon, VideoCameraIcon } from '@heroicons/react/24/solid';
import { useSwrJobItem } from '@hooks/useSwrApi/endpoints/job-operations';
import { Button } from '@components/common';
import { useSwrJobInterviewQuestions } from '@hooks/useSwrApi/endpoints/job';
import AIInterviewFlow from '@components/templates/ai-interview/AiInterviewFlow';
import JobBasedInterviewEndScreen from '@components/templates/ai-interview/JobBasedInterviewEndScreen';


interface JobBasedAiInterviewProps {
  jobSlug:string;
  locale: Locale;
  serverSideData?: ApiSchema<'InterviewQuestionListResult'>;
}

const JobAiInterview = (props: JobBasedAiInterviewProps) => {
  const { jobSlug, locale, serverSideData } = props;
  const { t } = useTranslation('common');
  const [currentInterview, setCurrentInterview] = useState<number>(0);

  const { data: pendingInterviews } = useSwrJobInterviewQuestions({
    locale,
    payload: { sortJobSlug: jobSlug, questionStatus: 'unanswered' },
    serverSideData,
  });

  const [currentJobIndex, setCurrentJobIndex] = useState(1); // Start from the first job (index 0) as we need it's title
  const jobItems = pendingInterviews?.items || [];
  const jobCount = jobItems.length;
  const currentJobSlug = jobItems[currentJobIndex]?.jobSlug || '';
  // Job Item for the job that's currently being interviewed
  const { data: currentJob } = useSwrJobItem({
    locale,
    jobSlug: jobItems[currentInterview]?.jobSlug || '',
  });
  // Job Item for the next job to be interviewed - showed on end screen
  const { data: jobItem, isValidating: isLoading } = useSwrJobItem({
    locale,
    jobSlug: currentJobSlug,
    shouldFetch: currentJobSlug !== '',
  });

  const onSkipJob = () => {
    // Only cycle through jobs from index 1 to jobCount-1 as index 0 is the interview that is already completed
    setCurrentJobIndex((prevIndex) => {
      if (jobCount <= 2) return 1; // If only 2 jobs, always stay at 1
      return prevIndex + 1;
    });
  };

  const onStartInterview = () => {
    setCurrentInterview(currentJobIndex);
  };

  return (
    <AIInterviewFlow
      key={`job-interview-${currentInterview}`}
      autostart
      questionsFromApi={pendingInterviews?.items?.at(currentInterview)?.questions || null}
      locale={locale}
      fileNamePrefix="jVideo"
      firebaseFolder="job_interview_video"
      timersConfiguration={{
        readCountdown: 20,
        recordingDuration: 60,
        timeBeforeManualStartEnabled: 5,
        timeBeforeManualNextEnabled: 10,
      }}
      endScreen={(
        <>
          <h2 className="mb-6 flex items-center gap-2 font-bold">
            <CheckCircleIcon className="size-6 text-secondary" />
            {t('job.video.interview.success.message', {
              jobTitle: currentJob?.title || '',
            })}
          </h2>
          <JobBasedInterviewEndScreen
            job={jobItem}
            isLoading={isLoading}
            startInterviewAction={(
              <Button
                styling="solid"
                color="secondary"
                rounding="full"
                className="flex-1 gap-2"
                onClick={onStartInterview}
              >
                <VideoCameraIcon className="size-5" />
                {t('ai.interview.start')}
              </Button>
            )}
            skipAction={
              jobCount <= 2 || currentJobIndex >= jobCount - 1 ? null : (
                <Button
                  styling="text"
                  color="primary"
                  className="flex-1 font-bold !no-underline"
                  onClick={onSkipJob}
                >
                  {t('job.video.interview.skip')}
                </Button>
              )
            }
          />
        </>
      )}
    />
  );
};

export default JobAiInterview;
